import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import { useTranslation } from 'react-i18next';
import { useActiveOrgStore } from '@store/index';
import { useBreadcrumbLabelStore } from '@store/breadcrumbLabels';

// Map of path segments to translation keys
const pathTranslationMap: Record<string, string> = {
  organizations: 'navigation.organizations',
  // The org-scoped routes use the '/orgs/:slug' prefix; label it the same as
  // the Organizations entry so the (single, top-level) breadcrumb doesn't read
  // a bare "Orgs".
  orgs: 'navigation.organizations',
  teams: 'navigation.teams',
  projects: 'navigation.projects',
  settings: 'navigation.settings',
  profile: 'navigation.profile',
  login: 'navigation.login',
  register: 'navigation.register',
  'reset-password': 'navigation.resetPassword',
  // REV-939: route segments are slugs, and title-casing a slug is not a label —
  // the rehearsal read "Contexts" and "Api-keys" where every other surface says
  // "Tracked Places" and "API Keys". Every static segment in App.tsx and the
  // tab names under it gets its real label here.
  contexts: 'navigation.crumbs.contexts',
  'api-keys': 'navigation.crumbs.apiKeys',
  members: 'navigation.crumbs.members',
  general: 'navigation.crumbs.general',
  'test-config': 'navigation.crumbs.testConfig',
  'audit-log': 'navigation.crumbs.auditLog',
  integrations: 'navigation.crumbs.integrations',
  findings: 'navigation.crumbs.findings',
  remediation: 'navigation.crumbs.remediation',
  reports: 'navigation.crumbs.reports',
  status: 'navigation.crumbs.status',
  audits: 'navigation.crumbs.audits',
  migrate: 'navigation.crumbs.migrate',
  bulk: 'navigation.crumbs.bulk',
  'import-history': 'navigation.crumbs.importHistory',
  'issues-breakdown': 'navigation.crumbs.issuesBreakdown',
  benchmark: 'navigation.crumbs.benchmark',
  criteria: 'navigation.crumbs.criteria',
  'manual-checklist': 'navigation.crumbs.manualChecklist',
  new: 'navigation.crumbs.new',
  tests: 'navigation.crumbs.tests',
  help: 'navigation.crumbs.help',
  legal: 'navigation.crumbs.legal',
  terms: 'navigation.crumbs.terms',
  privacy: 'navigation.crumbs.privacy',
  dpa: 'navigation.crumbs.dpa',
  billing: 'navigation.crumbs.billing',
  webhooks: 'navigation.crumbs.webhooks',
  password: 'navigation.crumbs.password',
  notifications: 'navigation.crumbs.notifications',
  appearance: 'navigation.crumbs.appearance',
  overview: 'navigation.crumbs.overview',
  scope: 'navigation.crumbs.scope',
  integrate: 'navigation.crumbs.integrate',
};

/**
 * Paths that appear as URL segments but are not routes of their own.
 *
 * REV-511: breadcrumbs were built by linkifying every path segment, so
 * /tests/new produced a "Tests" crumb pointing at /tests — which does not
 * exist and 404s. A breadcrumb that claims a destination it cannot reach is
 * worse than no breadcrumb: it invites a click that loses the user's place.
 *
 * These render as plain text instead. Audited against the route table in
 * App.tsx; the auth-layout paths (/auth, /invites, /reset-password) are not
 * listed because those screens render no breadcrumbs at all.
 *
 * Kept honest by Breadcrumbs.routes.test.tsx, which parses App.tsx and fails
 * if any entry here has since become a real route. Without that guard this
 * list silently rots — a path that gains a route stays permanently un-linked.
 */
export const NON_NAVIGABLE_PATHS: RegExp[] = [
  /^\/tests$/,
  /^\/consultant$/,
  /^\/consultant\/clients\/[^/]+$/, // only .../status exists beneath it
  /^\/orgs\/[^/]+\/reports$/, // only .../status exists beneath it (REV-897)
  // REV-929: only /audits/:id exists beneath it; the catch-all would render the
  // org's General tab. A place's audits are listed on its own Audits tab.
  /^\/orgs\/[^/]+\/audits$/,
  /^\/legal$/,
];

const isNavigable = (path: string) => !NON_NAVIGABLE_PATHS.some((re) => re.test(path));

interface BreadcrumbItem {
  path: string;
  label: string;
  isLast: boolean;
  navigable: boolean;
}

function Breadcrumbs() {
  const { t } = useTranslation();
  const location = useLocation();
  const activeOrg = useActiveOrgStore((state) => state.activeOrg);
  // REV-939: names published by the page that loaded the entity (a team, a
  // tracked place), keyed by the path of the crumb they name.
  const publishedLabels = useBreadcrumbLabelStore((state) => state.labels);

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  // Build breadcrumb items from path
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const breadcrumbItems: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    // A segment straight after /orgs or /teams is a slug the user chose, so it
    // is never looked up as a static label: an org called "billing" is still
    // an org, not the Billing page.
    const previous = pathSegments[index - 1];
    const isSlug = previous === 'orgs' || previous === 'teams';
    const translationKey = isSlug ? undefined : pathTranslationMap[segment];

    // If we have a translation key, use it; otherwise use the segment as-is
    // This handles dynamic segments like IDs
    let label: string;
    const published = publishedLabels[path];
    if (published) {
      label = published;
    } else if (activeOrg && previous === 'orgs' && segment === activeOrg.slug) {
      // Org slug segment — show the org's display name rather than the slug.
      label = activeOrg.name;
    } else if (translationKey) {
      label = t(translationKey);
    } else if (segment.match(/^[0-9a-f-]{36}$/i)) {
      // UUID - show as "Details" or similar
      label = t('navigation.details');
    } else {
      // Capitalize first letter for display
      label = segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    return {
      path,
      label,
      isLast: index === pathSegments.length - 1,
      navigable: isNavigable(path),
    };
  });

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" aria-hidden="true" />}
        aria-label={t('navigation.breadcrumbs')}
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: '1.25rem' }} aria-hidden="true" />
          {t('navigation.home')}
        </Link>

        {/* Path segments */}
        {breadcrumbItems.map((item) =>
          item.isLast || !item.navigable ? (
            <Typography
              key={item.path}
              color="text.primary"
              aria-current="page"
              sx={{ fontWeight: 500 }}
            >
              {item.label}
            </Typography>
          ) : (
            <Link
              key={item.path}
              component={RouterLink}
              to={item.path}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main',
                },
              }}
            >
              {item.label}
            </Link>
          )
        )}
      </MuiBreadcrumbs>
    </Box>
  );
}

export default Breadcrumbs;
